/**
 * Route chunk prefetching.
 *
 * Every page in Routes.tsx is `React.lazy(() => import(...))`, so the FIRST
 * visit to a route downloads its JS chunk on-click — that download is what
 * triggers the <Suspense> fallback (the loading flash users see on navigation).
 *
 * This warms every page chunk in the background once the app is idle. After a
 * few seconds of warmup, navigating to any page resolves its chunk from the
 * module cache synchronously — Suspense never shows a fallback, so navigation
 * feels instant with no spinner / skeleton at all.
 *
 * Vite turns `import.meta.glob` into the exact same dynamic-import chunks the
 * lazy() calls produce, so triggering them here populates the same cache.
 */

// Eager:false → returns a map of path -> () => import(path). Triggering the
// function downloads (but does not execute) the chunk.
// Excludes test files — this glob previously swept up every .test.ts(x) under
// src/pages/**, including __tests__ directories, and prefetchAllRoutes() actually
// executed their top-level code (dynamic import() runs the module). Any test file
// that references a real test-runner global (jest/vitest) throws in a browser
// context, since that runner was never loaded — this crashed app warmup outright
// once such a file existed (incident 2026-07-06, admin-staging stuck on splash).
const pageModules = import.meta.glob([
  "/src/pages/**/*.{tsx,ts}",
  "/src/kb/**/*.{tsx,ts}",
  "!/src/pages/**/*.test.{tsx,ts}",
  "!/src/pages/**/__tests__/**",
  "!/src/kb/**/*.test.{tsx,ts}",
  "!/src/kb/**/__tests__/**"
]);

let started = false;

const pageModuleEntries = Object.entries(pageModules);
const warmedHints = new Set<string>();

/**
 * Hover/focus-triggered prefetch for a single sidebar nav link.
 *
 * prefetchAllRoutes() eventually warms every page chunk, but it's staggered
 * across idle callbacks so a link clicked in the first second or two after
 * load can still miss the cache and show the Suspense fallback. This gives
 * that specific chunk a head start the moment the user shows intent (hover
 * or keyboard focus), well before the click/navigation actually happens.
 *
 * Best-effort by design: `routePath` (e.g. "/hubspot", "/ivr-campaigns") is
 * matched against the same file-path-keyed glob prefetchAllRoutes() uses, by
 * checking whether any meaningful path segment appears in the module's file
 * path. A miss just means no early warm for that link (falls back to the
 * general idle sweep); an extra/loose match just prefetches a harmless
 * neighboring chunk — every page chunk warms eventually regardless.
 */
export function prefetchRouteHint(routePath: string): void {
  if (!routePath || warmedHints.has(routePath)) return;
  warmedHints.add(routePath);

  const tokens = routePath
    .toLowerCase()
    .split(/[/\-_?:.]+/)
    .filter((t) => t.length > 2 && !["admin", "settings", "super"].includes(t));
  if (tokens.length === 0) return;

  for (const [key, loader] of pageModuleEntries) {
    const keyLower = key.toLowerCase();
    if (tokens.some((t) => keyLower.includes(t))) {
      loader().catch(() => {});
    }
  }
}

export function prefetchAllRoutes(): void {
  if (started) return;
  started = true;

  const loaders = Object.values(pageModules);

  // Stagger so the prefetch never competes with the data fetches of the page
  // the user is actually looking at — a few chunks per idle tick.
  let i = 0;
  const BATCH = 6;

  function pump() {
    if (i >= loaders.length) return;
    const batch = loaders.slice(i, i + BATCH);
    i += BATCH;
    Promise.all(batch.map((load) => load().catch(() => {}))).then(() => {
      schedule(pump);
    });
  }

  schedule(pump);
}

function schedule(fn: () => void): void {
  const ric = (window as any).requestIdleCallback as
    ((cb: () => void, opts?: { timeout: number }) => void) | undefined;
  if (ric) ric(fn, { timeout: 2000 });
  else window.setTimeout(fn, 200);
}
